import { useState, useEffect } from "react";
import {
    useCreateFlyerType,
    useUpdateFlyerType,
    type CampaignFlyer,
    type FlyerTypeCreateRequest,
    type FlyerTypeUpdateRequest,
} from "../api/campaigns";
import { ImageUpload } from "../components/ImageUpload";
import { showError, showSuccess } from "../shared/toast";
import "./flyer-type-form.css";

interface FlyerTypeFormProps {
    campaignId: string;
    campaignFlyer?: CampaignFlyer;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export const FlyerTypeForm = ({
    campaignId,
    campaignFlyer,
    onSuccess,
    onCancel,
}: FlyerTypeFormProps): JSX.Element => {
    const isEditMode = !!campaignFlyer;
    const [formData, setFormData] = useState<FlyerTypeCreateRequest>({
        name: campaignFlyer?.name || "",
        summary: campaignFlyer?.summary || "",
        imageUrl: campaignFlyer?.imageUrl || "",
        weightGrams: campaignFlyer?.weightGrams ?? undefined,
        isDefault: campaignFlyer?.isDefault || false,
        priority: campaignFlyer?.priority ?? 0,
        plannedQuantity: campaignFlyer?.plannedQuantity ?? undefined,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const createMutation = useCreateFlyerType(campaignId);
    const updateMutation = useUpdateFlyerType(campaignId);

    useEffect(() => {
        if (campaignFlyer) {
            setFormData({
                name: campaignFlyer.name || "",
                summary: campaignFlyer.summary || "",
                imageUrl: campaignFlyer.imageUrl || "",
                weightGrams: campaignFlyer.weightGrams ?? undefined,
                isDefault: campaignFlyer.isDefault,
                priority: campaignFlyer.priority ?? 0,
                plannedQuantity: campaignFlyer.plannedQuantity ?? undefined,
            });
        }
    }, [campaignFlyer]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name?.trim()) {
            newErrors.name = "Namn är obligatoriskt";
        }
        if (formData.plannedQuantity !== undefined && formData.plannedQuantity < 0) {
            newErrors.plannedQuantity = "Planerat antal måste vara 0 eller större";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            if (isEditMode && campaignFlyer) {
                const updateRequest: FlyerTypeUpdateRequest = {
                    isDefault: formData.isDefault,
                    priority: formData.priority,
                    plannedQuantity: formData.plannedQuantity,
                    imageUrl: formData.imageUrl || undefined,
                    name: formData.name,
                    summary: formData.summary,
                    weightGrams: formData.weightGrams,
                };
                await updateMutation.mutateAsync({
                    id: campaignFlyer.id,
                    ...updateRequest,
                });
            } else {
                const createRequest: FlyerTypeCreateRequest = {
                    name: formData.name,
                    summary: formData.summary,
                    imageUrl: formData.imageUrl || undefined,
                    weightGrams: formData.weightGrams,
                    isDefault: formData.isDefault,
                    priority: formData.priority,
                    plannedQuantity: formData.plannedQuantity,
                };
                await createMutation.mutateAsync(createRequest);
            }
            onSuccess?.();
            if (isEditMode) {
                showSuccess("Flygbladet har uppdaterats");
            } else {
                showSuccess("Flygbladet har lagts till");
            }
        } catch (error: any) {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                const message =
                    error?.response?.data?.message || "Ett fel uppstod";
                showError("Kunde inte spara flygbladet", message);
            }
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (name === "priority" || name === "plannedQuantity") {
            const numValue = value === "" ? undefined : parseInt(value, 10);
            setFormData((prev) => ({ ...prev, [name]: numValue }));
        } else if (name === "weightGrams") {
            const numValue = value === "" ? undefined : parseFloat(value);
            setFormData((prev) => ({ ...prev, [name]: numValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <form className="flyer-type-form" onSubmit={handleSubmit}>
            <h3>{isEditMode ? "Redigera flygblad i kampanj" : "Lägg till flygblad till kampanj"}</h3>

            <div className="form-group">
                <label htmlFor="name">
                    Namn <span aria-label="obligatoriskt">*</span>
                </label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name ?? ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    className={errors.name ? "error-field" : ""}
                />
                {errors.name && (
                    <span id="name-error" role="alert" className="error-message">
                        {errors.name}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="summary">Sammanfattning</label>
                <input
                    id="summary"
                    name="summary"
                    type="text"
                    value={formData.summary ?? ""}
                    onChange={handleChange}
                />
            </div>

            <div className="form-group">
                <label htmlFor="weightGrams">Vikt (gram)</label>
                <input
                    id="weightGrams"
                    name="weightGrams"
                    type="number"
                    value={formData.weightGrams ?? ""}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                />
            </div>

            <div className="form-group">
                <label htmlFor="isDefault">
                    <input
                        id="isDefault"
                        name="isDefault"
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={handleChange}
                    />
                    <span>Ange som standard för kampanjen</span>
                </label>
            </div>

            <div className="form-group">
                <label htmlFor="priority">Prioritet</label>
                <input
                    id="priority"
                    name="priority"
                    type="number"
                    value={formData.priority ?? ""}
                    onChange={handleChange}
                    min="0"
                    aria-invalid={!!errors.priority}
                    aria-describedby={errors.priority ? "priority-error" : undefined}
                    className={errors.priority ? "error-field" : ""}
                />
                {errors.priority && (
                    <span id="priority-error" role="alert" className="error-message">
                        {errors.priority}
                    </span>
                )}
                <small>Lägre nummer = högre prioritet</small>
            </div>

            <div className="form-group">
                <label htmlFor="plannedQuantity">Planerat antal</label>
                <input
                    id="plannedQuantity"
                    name="plannedQuantity"
                    type="number"
                    value={formData.plannedQuantity ?? ""}
                    onChange={handleChange}
                    min="0"
                    aria-invalid={!!errors.plannedQuantity}
                    aria-describedby={
                        errors.plannedQuantity ? "plannedQuantity-error" : undefined
                    }
                    className={errors.plannedQuantity ? "error-field" : ""}
                />
                {errors.plannedQuantity && (
                    <span
                        id="plannedQuantity-error"
                        role="alert"
                        className="error-message"
                    >
                        {errors.plannedQuantity}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label>Bild</label>
                <ImageUpload
                    value={formData.imageUrl ?? ""}
                    onChange={(url) => {
                        setFormData((prev) => ({ ...prev, imageUrl: url }));
                    }}
                    disabled={isLoading}
                />
                <p className="form-hint">
                    Ladda upp en bild för flygbladet.
                </p>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    className="button button--secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Avbryt
                </button>
                <button
                    type="submit"
                    className="button button--primary"
                    disabled={isLoading}
                >
                    {isLoading ? "Sparar..." : isEditMode ? "Spara" : "Lägg till"}
                </button>
            </div>
        </form>
    );
};